import { useState, useCallback, useMemo } from 'react';
import { useCancelBatch } from '~/modules/common/hooks';
import { useMeContext } from '~/modules/me';
import { INITIAL_MOVE_TIME_DATA, MOVE_TIME_STEP_INDICES } from '../constants';
import { useTimeEntrySummary } from './useTimeEntrySummary';
import { useMoveTimeBatch } from './useMoveTimeBatch';
import { useMoveTimeBatchResults } from './useMoveTimeBatchResults';
import { useCriteriaToFilter } from './useCriteriaToFilter';

export const useMoveTimeDialogHandlers = ({ onClose }) => {
  const { hasViewProjectBillingOptions } = useMeContext();

  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [batchState, setBatchState] = useState({
    batchId: null,
    batchInProgress: false
  });
  const [moveTimeData, setMoveTimeData] = useState({
    ...INITIAL_MOVE_TIME_DATA,
    source: {
      ...INITIAL_MOVE_TIME_DATA.source,
      project: null,
      task: null,
      filter: {
        showInactiveProjects: false,
        showClosedTasks: false
      }
    },
    billingStatus: null
  });
  const [timeEntrySummary, setTimeEntrySummary] = useState({
    loading: false,
    count: 0,
    totalHours: 0
  });
  const [batchResults, setBatchResults] = useState(null);

  const additionalFilters = useCriteriaToFilter(moveTimeData.criteria || []);

  const cancelBatch = useCancelBatch();
  const { refetch: fetchBatchResults } = useMoveTimeBatchResults();

  const queryResult = useTimeEntrySummary({
    projectId: moveTimeData.source.project?.id,
    taskId: moveTimeData.source.task?.isNoTask
      ? undefined
      : moveTimeData.source.task?.id,
    additionalFilters,
    skip:
      activeStep > MOVE_TIME_STEP_INDICES.SELECTION ||
      !moveTimeData.source.project?.id ||
      !moveTimeData.source.task
  });

  const { createMoveTimeBatch } = useMoveTimeBatch({
    setBatchState
  });

  const currentSummary =
    activeStep > MOVE_TIME_STEP_INDICES.SELECTION
      ? timeEntrySummary
      : queryResult;

  const handleBack = useCallback(() => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  }, [activeStep]);

  const handleClose = useCallback(
    (event, reason) => {
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;

      setActiveStep(0);
      setMoveTimeData({ ...INITIAL_MOVE_TIME_DATA });
      setBatchState({ batchId: null, batchInProgress: false });
      setTimeEntrySummary({ loading: false, count: 0, totalHours: 0 });
      onClose(event, reason);
    },
    [onClose]
  );

  const handleNext = useCallback(async () => {
    if (activeStep === MOVE_TIME_STEP_INDICES.SELECTION) {
      setTimeEntrySummary({
        loading: queryResult.loading,
        count: queryResult.count,
        totalHours: queryResult.totalHours
      });
      setActiveStep(activeStep + 1);
    } else if (activeStep === MOVE_TIME_STEP_INDICES.DESTINATION) {
      if (isSubmitting) return;

      setIsSubmitting(true);
      try {
        await createMoveTimeBatch({
          sourceProjectId: moveTimeData.source.project.id,
          sourceTaskId: moveTimeData.source.task?.isNoTask
            ? undefined
            : moveTimeData.source.task?.id,
          destinationProjectId: moveTimeData.destination.project.id,
          destinationTaskId: moveTimeData.destination.task?.isNoTask
            ? undefined
            : moveTimeData.destination.task?.id,
          billingStatus: hasViewProjectBillingOptions
            ? moveTimeData.billingStatus
            : undefined,
          additionalFilters
        });
        setActiveStep(MOVE_TIME_STEP_INDICES.PROGRESS);
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [
    activeStep,
    isSubmitting,
    queryResult,
    createMoveTimeBatch,
    moveTimeData.source.project,
    moveTimeData.source.task,
    moveTimeData.destination.project,
    moveTimeData.destination.task,
    moveTimeData.billingStatus,
    additionalFilters,
    hasViewProjectBillingOptions
  ]);

  const handleBatchComplete = useCallback(async () => {
    const result = await fetchBatchResults({ batchId: batchState.batchId });
    const {
      data: {
        getTimeEntryRevisionGroupBulkModificationBatchResults: {
          modified = [],
          errors = []
        } = {}
      } = {}
    } = result;

    setBatchResults({
      successCount: modified.length,
      errorCount: errors.length
    });
    setBatchState(prev => ({ ...prev, batchInProgress: false }));
    setActiveStep(MOVE_TIME_STEP_INDICES.RESULT);
  }, [batchState.batchId, fetchBatchResults]);

  const handleCancelBatch = useCallback(async () => {
    if (batchState?.batchId && batchState?.batchInProgress) {
      await cancelBatch(batchState?.batchId);
      handleBatchComplete();
    }
  }, [
    batchState.batchId,
    batchState?.batchInProgress,
    cancelBatch,
    handleBatchComplete
  ]);

  const handleMoveMoreTimeEntries = useCallback(() => {
    setActiveStep(MOVE_TIME_STEP_INDICES.SELECTION);
    setMoveTimeData({ ...INITIAL_MOVE_TIME_DATA });
    setBatchState({ batchId: null, batchInProgress: false });
    setTimeEntrySummary({ loading: false, count: 0, totalHours: 0 });
  }, []);

  const isNextDisabled = useMemo(() => {
    if (isSubmitting) {
      return true;
    }

    switch (activeStep) {
      case MOVE_TIME_STEP_INDICES.SELECTION:
        return !(
          moveTimeData.source.project &&
          !currentSummary.loading &&
          currentSummary.count > 0
        );
      case MOVE_TIME_STEP_INDICES.DESTINATION:
        return !(
          moveTimeData.destination.project &&
          moveTimeData.destination.task &&
          (hasViewProjectBillingOptions ? moveTimeData.billingStatus : true)
        );
      default:
        return false;
    }
  }, [
    activeStep,
    isSubmitting,
    moveTimeData,
    currentSummary,
    hasViewProjectBillingOptions
  ]);

  return {
    activeStep,
    isNextDisabled,
    isSubmitting,
    moveTimeData,
    setMoveTimeData,
    handleNext,
    handleBack,
    handleClose,
    handleBatchComplete,
    handleCancelBatch,
    handleMoveMoreTimeEntries,
    timeEntrySummary: currentSummary,
    batchState,
    batchResults
  };
};

export default useMoveTimeDialogHandlers;
